import { Events, emit } from './store-events.js';

// Lightweight accordion controller component
// Usage:
// <store-accordion data-accordion="collapse"> ... rows ... </store-accordion>
// Rows should include:
//  - a button with [data-acc-button][data-accordion-target="#panel-id"][aria-expanded]
//  - a panel matching the selector with [aria-hidden] and classes toggled between grid-rows-[0fr]/grid-rows-[1fr]

class StoreAccordion extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }
  connectedCallback() {
    try {
      this.addEventListener('click', this._onClick, true);
      this._normalizeInitialState();
    } catch (_) {}
  }
  disconnectedCallback() {
    try { this.removeEventListener('click', this._onClick, true); } catch (_) {}
  }
  _setState(btn, pnl, open) {
    try {
      if (btn) btn.setAttribute('aria-expanded', String(!!open));
      if (pnl) pnl.setAttribute('aria-hidden', String(!open));
      if (pnl && pnl.classList) {
        pnl.classList.remove('grid-rows-[0fr]', 'grid-rows-[1fr]');
        pnl.classList.add(open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]');
      }
      const row = btn && btn.closest && btn.closest('[data-acc-row]');
      if (row) row.setAttribute('data-acc-state', open ? 'open' : 'closed');
      const plus = btn && btn.querySelector && btn.querySelector('[data-acc-icon-plus]');
      const minus = btn && btn.querySelector && btn.querySelector('[data-acc-icon-minus]');
      if (plus && minus) {
        if (open) { plus.classList.add('hidden'); minus.classList.remove('hidden'); }
        else { plus.classList.remove('hidden'); minus.classList.add('hidden'); }
      }
    } catch (_) {}
  }
  _normalizeInitialState() {
    try {
      const mode = (this.getAttribute('data-accordion') || 'collapse').toLowerCase();
      const collapse = mode !== 'multiple'; // default single-open behavior
      if (!collapse) return;
      const buttons = Array.from(this.querySelectorAll('[data-acc-button]'));
      if (buttons.length <= 1) return;
      let foundOpen = false;
      buttons.forEach((btn) => {
        const sel = btn.getAttribute('data-accordion-target');
        const pnl = sel && this.querySelector(sel);
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        if (!foundOpen && isExpanded) {
          foundOpen = true;
          this._setState(btn, pnl, true);
        } else {
          this._setState(btn, pnl, false);
        }
      });
    } catch (_) {}
  }
  _onClick(ev) {
    try {
      let accBtn = ev.target && (ev.target.closest && ev.target.closest('[data-acc-button]'));
      if (!accBtn || !this.contains(accBtn)) {
        // Allow clicking anywhere on the row wrapper to toggle (e.g., padding around the button)
        const row = ev.target && ev.target.closest && ev.target.closest('[data-acc-row]');
        if (!row || !this.contains(row)) return;
        // Ignore clicks originating inside the panel/region content
        const panelInRow = row.querySelector('[role="region"]');
        if (panelInRow && panelInRow.contains(ev.target)) return;
        accBtn = row.querySelector('[data-acc-button]');
        if (!accBtn) return;
      }
      const targetSel = accBtn.getAttribute('data-accordion-target');
      if (!targetSel) return;
      const panel = this.querySelector(targetSel);
      if (!panel) return;
      const isExpanded = accBtn.getAttribute('aria-expanded') === 'true';
      const mode = (this.getAttribute('data-accordion') || 'collapse').toLowerCase();
      const collapse = mode !== 'multiple'; // 'collapse' or 'single' => close others; 'multiple' => allow many
      if (collapse) {
        const buttons = Array.from(this.querySelectorAll('[data-acc-button]'));
        buttons.forEach((btn) => {
          if (btn === accBtn) return;
          const sel = btn.getAttribute('data-accordion-target');
          const pnl = sel && this.querySelector(sel);
          if (pnl) this._setState(btn, pnl, false);
        });
      }
      this._setState(accBtn, panel, !isExpanded);
      ev.preventDefault && ev.preventDefault();
      ev.stopPropagation && ev.stopPropagation();
    } catch (_) {}
  }
}

try {
  const TAG = 'store-accordion';
  if (!customElements.get(TAG)) customElements.define(TAG, StoreAccordion);
} catch (_) {}


